import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '@result/result-ui-components/src/components/Button';
import Input from '@result/result-ui-components/src/components/Input';
import DateTimePicker from '@result/result-ui-components/src/components/DateTimePicker';
import Fragment from '@result/result-ui-components/src/components/Fragment';
import Checkbox from '@result/result-ui-components/src/components/Checkbox';
import Currency from '@result/result-ui-components/src/components/Currency';
import PropTypes from 'prop-types';
import { useForm } from '@result/result-ui-components/src/hooks/form/formHook';
import { useTranslation } from 'react-i18next';
import _, { get, isEmpty } from 'lodash';
import { stringToDate } from '@result/result-ui-components/src/utils/DateUtils';
import { closestParentByClass } from '@result/result-ui-components/src/utils/DomUtils';
import useUpdateOnlyEffect from '@result/result-ui-components/src/hooks/effect/useUpdateOnlyEffect';
import Select from '@result/result-ui-components/src/components/Select';
import InputTagFilter from './InputTagFilter';

const Filter = (props) => {
	const { t } = useTranslation();
	const [filterOpen, setFilterOpen] = useState(false);
	const [filter, setFilter] = useState([]);

	const drawerElement = useRef({
		querySelector: () => {},
	});

	const { values, setValue, setValues, setFormNotDirty, getField } = useForm();


	const onOutsideFilterClick = useCallback((event) => {
		if (
			drawerElement.current.classList.contains('open') &&
			event.target.parentElement != null &&
			closestParentByClass('table-filter', event.target) === null
		) {
			setFilterOpen(false);
		}
	}, []);

	useEffect(() => {
		if(props.initialFilter == null || _.isEmpty(props.initialFilter)) {
			return;
		}
		setValues(props.initialFilter);
	}, [props.initialFilter]);

	useEffect(() => {
		document.addEventListener('click', onOutsideFilterClick);
		return () => document.removeEventListener('click', onOutsideFilterClick);
	}, []);

	useEffect(() => {
		// autofocus first input in the drawer
		filterOpen &&
			drawerElement.current
				.querySelector('.first-filter .field:not(.dropdown) input')
				?.focus();
	}, [filterOpen]);

	useUpdateOnlyEffect(() => {
		const filter = props.columns.reduce((filter, column) => {
			const value = get(values, column.id);
			let from = value?.from;
			let to = value?.to;

			if (
				!Number.isInteger(value) &&
				typeof value !== 'boolean' &&
				isEmpty(value)
			) {
				//isEmpty is returning true for integers and booleans
				return filter;
			}

			switch (column.type) {
				case 'string':
				case 'numericString':
					filter.push({
						name: column.id,
						operator: 'LIKE_IC',
						value: value,
					});
					break;
				case 'select':
					if (String(value) === 'null') {
						filter.push({
							name: column.id,
							operator: 'ISNULL',
						});
						break;
					}
					filter.push({
						name: column.id,
						operator: 'EQ',
						value: value,
					});
					break;
				case 'multiselect':
					filter.push({
						name: column.id,
						operator: 'EQ',
						value: value.join(` OR ${column.id}:EQ:`),
					});
					break;
				case 'date':
				case 'datetime':
					if (isEmpty(from) && isEmpty(to)) {
						break;
					}

					stringToDate(from) > stringToDate(to) && ([to, from] = [from, to]); //reverse variable values
					if (!isEmpty(from)) {
						filter.push({
							name: column.id,
							operator: 'GTE',
							value: from,
						});
					}
					if (!isEmpty(to)) {
						filter.push({
							name: column.id,
							operator: 'LTE',
							value: to,
						});
					}
					break;
				case 'number':
				case 'currency':
					if (from == null && to == null) {
						break;
					}

					parseFloat(from) > parseFloat(to) && ([to, from] = [from, to]); //reverse variable values
					if (from != null) {
						filter.push({
							name: column.id,
							operator: 'GTE',
							value: from,
						});
					}
					if (to != null) {
						filter.push({
							name: column.id,
							operator: 'LTE',
							value: to,
						});
					}
					break;
				case 'boolean':
					filter.push({
						name: column.id,
						operator: 'EQ',
						value: String(value),
					});
					break;
				case 'inputtag':
					filter.push({
						name: column.id,
						operator: column.filter || 'LIKE_IC',
						value: value.map(val => val.name).join(
							` OR ${column.id}:${column.filter || 'LIKE_IC'}:`
						),
					});
					break;
				default:
					return {};
			}
			return filter;
		}, []);
		setFilter(filter);
		props.onFilter && props.onFilter(filter, values);
	}, [values]);

	useEffect(() => {
		// only update filter when existing filter is empty (this means we are returned to the page)
		if (props.filter && props.filter.length > 0 && filter.length === 0) {
			const columnMap = props.columns.reduce((map, column) => {
				map[column.id] = column;
				return map;
			}, {});

			props.filter.forEach((filter) => {
				const column = columnMap[filter.name];
				let value;
				if (!column) {
					return;
				}

				switch (column.type) {
					case 'string':
					case 'numericString':
						value = filter.value.replace(/^'+|'+$/gm, '').replace(/\\'/gm, "'");
						values[column.id] !== value && setValue(column.id, value);
						break;
					case 'select':
						filter.operator === 'EQ' && setValue(column.id, filter.value);
						break;
					case 'date':
					case 'datetime':
						if (filter.operator === 'GTE') {
							setValue(column.id + '.from', filter.value);
						} else if (filter.operator === 'LTE') {
							setValue(column.id + '.to', filter.value);
						}
						break;
					case 'number':
					case 'currency':
						if (filter.operator === 'GTE') {
							setValue(column.id + '.from', filter.value);
						} else if (filter.operator === 'LTE') {
							setValue(column.id + '.to', filter.value);
						}
						break;
					case 'boolean':
						filter.operator === 'EQ' &&
							setValue(column.id, filter.value === 'true');
						break;
					default:
						return {};
				}
			});
		}
	}, [props.filter]);

	const exportData = (filter) => {
		props.onExport && props.onExport(filter);
	};

	return (
		<div className="table-filter ignore-loading">
			<Fragment rendered={!!props.onExport}>
				<Button
					className="fz fz-file-export large"
					isOutlined={true}
					isLoading={props.isExporting}
					onClick={() => exportData(filter)}
				/>
			</Fragment>
			<div className="is-relative is-inline-flex">
				<Button
					className="fz fz-filter1 large marginless filter-button"
					color={props.filter.length > 0 ? 'primary' : 'default'}
					isOutlined={true}
					disabled={props.disabled}
					onClick={() => setFilterOpen(!filterOpen)}
				/>
				{props.filter.length > 0 && (
					<span className="filter-counter">{props.filter.length}</span>
				)}
			</div>
			<div
				ref={drawerElement}
				className={
					filterOpen
						? 'table-filter-drawer card open'
						: 'table-filter-drawer card'
				}>
				<div className="card-header-title">
					<span className="flex-grow-1 is-size-4">{t('Filter')}</span>
					<Button
						className="fz fz-times marginless"
						color="danger"
						onClick={() => setFilterOpen(false)}
						isOutlined={true}
					/>
				</div>
				<div className="card-content">
					{props.columns.map((column, index) => (
						<div
							key={column.id}
							className={index === 0 ? 'columns first-filter' : 'columns'}>
							{column.type === 'string' && (
								<div className="column">
									<Input
										label={t(column.name)}
										formField={getField(column.id)}
									/>
								</div>
							)}
							{column.type === 'numericString' && (
								<div className="column">
									<Input
										label={t(column.name)}
										formField={getField(column.id)}
										type="number"
									/>
								</div>
							)}
							{column.type === 'date' && (
								<React.Fragment>
									<div className="column">
										<DateTimePicker
											label={t(column.name + '_from')}
											formField={getField(column.id + '.from')}
										/>
									</div>
									<div className="column">
										<DateTimePicker
											label={t(column.name + '_to')}
											formField={getField(column.id + '.to')}
										/>
									</div>
								</React.Fragment>
							)}
							{column.type === 'datetime' && (
								<React.Fragment>
									<div className="column">
										<DateTimePicker
											label={t(column.name + '_from')}
											formField={getField(column.id + '.from')}
											type="datetime"
										/>
									</div>
									<div className="column">
										<DateTimePicker
											label={t(column.name + '_to')}
											formField={getField(column.id + '.to')}
											type="datetime"
										/>
									</div>
								</React.Fragment>
							)}
							{column.type === 'currency' && (
								<React.Fragment>
									<div className="column">
										<Currency
											label={t(column.name + '_from')}
											formField={getField(column.id + '.from')}
										/>
									</div>
									<div className="column">
										<Currency
											label={t(column.name + '_to')}
											formField={getField(column.id + '.to')}
										/>
									</div>
								</React.Fragment>
							)}
							{column.type === 'number' && (
								<React.Fragment>
									<div className="column">
										<Input
											type="number"
											label={t(column.name + '_from')}
											formField={getField(column.id + '.from')}
										/>
									</div>
									<div className="column">
										<Input
											type="number"
											label={t(column.name + '_to')}
											formField={getField(column.id + '.to')}
										/>
									</div>
								</React.Fragment>
							)}
							{column.type === 'select' && (
								<div className="column">
									<Select
										label={t(column.name)}
										formField={getField(column.id)}
										options={column.options}
										optionsLabel={column.optionsLabel}
										emptyOption={true}
									/>
								</div>
							)}
							{column.type === 'multiselect' && (
								<div className="column">
									<Select
										label={t(column.name)}
										formField={getField(column.id)}
										options={column.options}
										optionsLabel={column.optionsLabel}
										emptyOption={false}
										multiSelectOptions={column.multiSelectOptions}
										multiselectValues={column.multiselectValues}
										multiselectCodes={column.multiselectCodes}
										multiselectItems={column.multiselectItems}
										isMultiSelect={true}
										onSearchQueryChange={column.onSearchQueryChange}
									/>
								</div>
							)}
							{column.type === 'boolean' && (
								<div className="column">
									<Checkbox
										label={t(column.name)}
										formField={getField(column.id)}
										type="form"
										color="auto"
										threeStateMode={true}
									/>
								</div>
							)}
							{column.type === 'inputtag' && (
								<div className="column">
									<InputTagFilter
										filtersActive={props.filter.length === 0 ? false : true}
										label={t(column.name)}
										formField={getField(column.id)}
										id={column.id}
										fetchAction={column.fetchAction}
										selector={column.selector}
										isTranslated={column.isTranslated}
										translationKey={column.translationKey}
										valueType={column.valueType}
										searchKey={column.searchKey}
										filterKey={column.filterKey}
										displayKey={column.displayKey}
									/>
								</div>
							)}
						</div>
					))}

					<Button
						label={t('remove_filters')}
						icon="fz-trash-alt"
						color="danger"
						isOutlined={true}
						onClick={() => setFormNotDirty(true)}
						disabled={props.filter.length === 0}
					/>
				</div>
			</div>
		</div>
	);
};

Filter.propTypes = {
	columns: PropTypes.array.isRequired,
	filter: PropTypes.array.isRequired,
	onFilter: PropTypes.func.isRequired,
	onExport: PropTypes.func,
	isExporting: PropTypes.bool,
	disabled: PropTypes.bool,
	initialFilter: PropTypes.object,
};

export default Filter;
